import { $can, $canSub } from '@pendo/permissions';
import {
    canCreateEmailFromLayout,
    canCreateEmailFromScratch,
    canPublishEmail,
    canUpdateEmail,
    canDeleteEmail,
} from '../modules/emails/emails-permissions.utils';

/*
 * Keep in mind that string visitor metadata fields have a soft limit of 1500 characters.
 * Longer strings can be stored, but certain functionality (like searching) may be limited.
 * Consider only storing the most relevant permissions in the visitor metadata.
 */
const CHECKS = [
    {
        label: 'Create Features',
        check: () =>
            $can('ANY')
                .entity('feature')
                .action('create'),
    },
    {
        label: 'Delete Features',
        check: () =>
            $can('ANY')
                .entity('feature')
                .action('remove'),
    },
    {
        label: 'Create Pages',
        check: () =>
            $can('ANY')
                .entity('page')
                .action('create'),
    },
    {
        label: 'Delete Pages',
        check: () =>
            $can('ANY')
                .entity('page')
                .action('remove'),
    },
    {
        label: 'Verify Segments',
        check: () =>
            $canSub()
                .entity('segment')
                .action('verification')
                .source('*')
                .target('verified'),
    },
    {
        label: 'Manage Core Events',
        check: () =>
            $can('ANY')
                .entity('coreEvent')
                .action('edit'),
    },
    {
        label: 'Manage PES',
        check: () =>
            $can('ANY')
                .entity('pesConfiguration')
                .action('edit'),
    },
    {
        label: 'Create Guides',
        check: () =>
            $can('ANY')
                .entity('guide')
                .action('create')
                .source('ANY')
                .target('ANY'),
    },
    {
        label: 'Edit Guides',
        check: () =>
            $can('ANY')
                .entity('guide')
                .action('edit')
                .source('ANY')
                .target('ANY'),
    },
    {
        label: 'Publish Guides',
        check: () =>
            $can('ANY')
                .entity('guide')
                .action('publish')
                .source('ANY')
                .target('ANY'),
    },
    {
        label: 'Create Emails',
        check: () => canCreateEmailFromLayout({ appId: 'ANY' }) || canCreateEmailFromScratch({ appId: 'ANY' }),
    },
    {
        label: 'Edit Emails',
        check: () => canUpdateEmail({ appId: 'ANY', source: 'ANY' }),
    },
    {
        label: 'Publish Emails',
        check: () => canPublishEmail({ appId: 'ANY' }),
    },
    {
        label: 'Delete Emails',
        check: () => canDeleteEmail({ appId: 'ANY', source: 'ANY' }),
    },
    {
        label: 'Create Feedback items',
        check: () =>
            $canSub()
                .entity('vocItemFeedback')
                .action('create'),
    },
    {
        label: 'Edit Feedback items',
        check: () =>
            $canSub()
                .entity('vocItemFeedback')
                .action('edit'),
    },
    {
        label: 'Create Roadmaps',
        check: () =>
            $canSub()
                .entity('roadmap')
                .action('create'),
    },
    {
        label: 'Edit Roadmaps',
        check: () =>
            $canSub()
                .entity('roadmap')
                .action('edit'),
    },
    {
        label: 'Create Idea tests',
        check: () =>
            $canSub()
                .entity('ideaPoll')
                .action('create'),
    },
    {
        label: 'Create Idea portals',
        check: () =>
            $canSub()
                .entity('vocPortal')
                .action('create'),
    },
    {
        label: 'Publish Roadmaps',
        check: () =>
            $canSub()
                .entity('roadmap')
                .action('publish'),
    },
    {
        label: 'Create NPS',
        check: () =>
            $can('ANY')
                .entity('nps')
                .action('create')
                .source('ANY')
                .target('ANY'),
    },
    {
        label: 'Edit NPS',
        check: () =>
            $can('ANY')
                .entity('nps')
                .action('edit')
                .source('ANY')
                .target('ANY'),
    },
    {
        label: 'Publish NPS',
        check: () =>
            $can('ANY')
                .entity('nps')
                .action('publish')
                .source('ANY')
                .target('ANY'),
    },
    {
        label: 'Triage NPS themes',
        check: () =>
            $can('ANY')
                .entity('npsTheme')
                .action('triage'),
    },
    {
        label: 'Create Resource Center',
        check: () =>
            $can('ANY')
                .entity('resourceCenter')
                .action('create')
                .source('ANY')
                .target('ANY'),
    },
    {
        label: 'Edit Resource Center',
        check: () =>
            $can('ANY')
                .entity('resourceCenter')
                .action('edit')
                .source('ANY')
                .target('ANY'),
    },
    {
        label: 'Promote Resource Center',
        check: () =>
            $can('ANY')
                .entity('resourceCenter')
                .action('publish')
                .source('ANY')
                .target('ANY'),
    },
    {
        label: 'Replay User',
        check: () =>
            $can('ANY')
                .entity('sessionReplay')
                .action('view'),
    },
];

export function getPermissionString (checks = CHECKS) {
    return checks
        .filter((check) => check.check())
        .map(({ label }) => label)
        .join(', ');
}
